import React from 'react';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';

import { Breadcrumb } from './Breadcrumb';
import { BreadcrumbDivider, BreadcrumbDividerSymbol } from './BreadcrumbDivider';
import { BreadcrumbInfo } from './BreadcrumbInfo';
import { BreadcrumbStyles } from './BreadcrumbStyles';

export namespace Breadcrumbs {
  export interface StateProps {
    items: ReadonlyArray<BreadcrumbInfo>;
    linkLastItem: boolean;
  }

  export interface Props extends StateProps {}
}

export function Breadcrumbs(props: Breadcrumbs.Props): React.ReactElement<Breadcrumbs.Props> {
  const items = condenseBreadcrumbs(props.items);
  const dividers: JSX.Element[] = _.times(items.length - 1, n => <BreadcrumbDivider key={'breadcrumb-divider-' + n}/>);
  const breadcrumbs: JSX.Element[] = _.map(items, (item, index, items) =>
     <Breadcrumb key={'breadcrumb-'+index}
                 text={item.text}
                 href={(index < items.length - 1 || props.linkLastItem) ? item.href : null}
                 tooltip={item.tooltip}
                 testId={item.testId} />
   );

  const containerStyle = BreadcrumbStyles.BreadcrumbContainerInTopBar;

  // breadcrumbs show when there is more that one crumb, or if props.linkLastItem is true
  if(props.items.length > 1 || props.linkLastItem) {
    return (
      <nav aria-label={Messages.breadcrumbAriaLabel()} className={containerStyle}>
        <ol className={BreadcrumbStyles.BreadcrumbList} data-tb-test-id="breadcrumb-list">
          {_.zip(breadcrumbs, dividers)}
        </ol>
      </nav>
    );
  }
  else {
    return null;
  }
}

/**
 * Condenses the given list of breadcrumbs except for the given number of items on either end of the list. The condensed
 * items are combined into a single ellipsis breadcrumb item in the middle of the list. If the number of items in the
 * list is equal to or less than the number of items that would be present after condensing, then no condensing occurs.
 *
 * @param items           breadcrumb items to truncate
 * @param numItemsToKeep  number of breadcrumbs to show on each end when truncated
 * @returns               properly truncated list of breadcrumb items
 */
export function condenseBreadcrumbs(items: ReadonlyArray<BreadcrumbInfo>, numItemsToKeep = 2): ReadonlyArray<BreadcrumbInfo> {
  const maxItems = numItemsToKeep * 2 + 1; // max number of breadcrumbs to show without being truncated

  if (items.length <= maxItems) {
    return items;
  }

  // the truncation item tooltip consists of all of the items being truncated separated by the breadcrumb separator
  const itemTexts = _.map(items, i => i.text);
  const truncationItemTexts = itemTexts.slice(numItemsToKeep, items.length - numItemsToKeep);
  const truncationTooltip = truncationItemTexts.join(` ${BreadcrumbDividerSymbol} `);

  const truncationItem: BreadcrumbInfo = {
    text: '\u2026', // ellipsis
    tooltip: truncationTooltip
  };

  return _.first(items, numItemsToKeep).concat(truncationItem).concat(_.last(items, numItemsToKeep));
}
